import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { inject, observer } from 'mobx-react';

import { RouteStore, QueryRoute, AnalyticsStore } from 'bernie-core';
import { Layer } from 'bernie-view';
import { DayName, UitkNewDatePicker, UitkNewDatePickerProps } from 'uitk-react-new-date-picker';

import { trackSearchFormAnalytics } from 'utility';
import { AnalyticsType, InteractableFieldComponentProps } from 'types/shared';
import { FlightsSearchFormDateFieldType } from 'types/search/wizard';

export interface DatePickerProps extends InteractableFieldComponentProps {
  startDate: FlightsSearchFormDateFieldType;
  flightType: string;
  identifier: string;
  endDate?: FlightsSearchFormDateFieldType;
  routeStore?: RouteStore;
  singleMode: boolean;
  onSubmit: (selectedDepartureDate: Date, selectedReturnDate: Date) => void;
  buttonText?: string;
  singleDateSelect?: boolean;
  onSetDatePickerRefs?: (refData: React.Ref<HTMLButtonElement>[]) => void;
  validStartDate?: string;
  nestedInDialog?: boolean;
  tripIndex?: number;
  analytics?: AnalyticsStore;
  onClickAnalytics?: AnalyticsType[];
  onFieldInteraction?: () => void;
}

interface DatePickerState {
  showDatePicker: boolean;
  showSingleDatePicker: boolean;
}

@inject('routeStore', 'analytics')
@observer
export class DatePicker extends React.Component<DatePickerProps, DatePickerState> {
  constructor(props: DatePickerProps) {
    super(props);

    this.state = {
      showDatePicker: false,
      showSingleDatePicker: false
    };
  }

  public UNSAFE_componentWillReceiveProps(nextProps: DatePickerProps) {
    const { flightType, endDate } = this.props;

    if (flightType !== nextProps.flightType) {
      endDate.date = null;
      endDate.currentError = '';
    }
  }

  private dialogWrapper = (children: React.ReactNode, dateType: string) => {
    const { nestedInDialog } = this.props;
    const dialogQuery = {};

    dialogQuery[nestedInDialog ? 'pwaDialogNested' : 'pwaDialog'] = 'wizardDatepickerDialog';

    return (
      <QueryRoute query={dialogQuery}>
        <Layer id={`wizardDatepickerDialog-${dateType}`} onDismiss={this.onDismissCB}>
          {children}
        </Layer>
      </QueryRoute>
    );
  };

  private toggleDatePicker = () => {
    this.setState({
      showDatePicker: !this.state.showDatePicker
    });
  };

  private onSubmitCB = (isMobile: boolean) => {
    if (isMobile) {
      this.props.routeStore.closeDialog();
    }

    this.toggleDatePicker();
  };

  private onDismissCB = (isMobile: boolean, noUpdate?: boolean) => {
    if (isMobile) {
      this.props.routeStore.closeDialog();
    }

    if (!noUpdate) {
      this.toggleDatePicker();
    }
  };

  private onOpenCB = (name: string) => {
    const { onFieldInteraction } = this.props;
    onFieldInteraction && onFieldInteraction();
    this.setState({ showDatePicker: true });
    const { analytics, onClickAnalytics, flightType, tripIndex } = this.props;
    if (onClickAnalytics) {
      name === 'start'
        ? trackSearchFormAnalytics(analytics, onClickAnalytics[0], flightType, tripIndex + 1)
        : trackSearchFormAnalytics(analytics, onClickAnalytics[1], flightType);
    }
  };

  private catchRefs(startRef: React.Ref<HTMLButtonElement>, endRef: React.Ref<HTMLButtonElement>) {
    this.props.onSetDatePickerRefs && this.props.onSetDatePickerRefs([startRef, endRef]);
  }

  public render() {
    const {
      startDate,
      endDate,
      routeStore,
      onSubmit,
      identifier,
      singleMode,
      buttonText,
      singleDateSelect = true,
      validStartDate
    } = this.props;

    const routerLinkWrapper = (children: React.ReactNode) => {
      const { nestedInDialog } = this.props;
      const dialogQuery = {};

      dialogQuery[nestedInDialog ? 'pwaDialogNested' : 'pwaDialog'] = 'wizardDatepickerDialog';

      return (
        <RouterLink
          to={{
            pathname: routeStore.pathname,
            search: routeStore.mergeQueryString(routeStore.search, dialogQuery)
          }}
        >
          {children}
        </RouterLink>
      );
    };

    const dateFieldProps = {
      endId: singleMode ? '' : `end-date-${identifier}`,
      endInvalid: singleMode ? '' : endDate.currentError,
      endLabel: singleMode ? '' : endDate.label,
      endName: singleMode ? '' : `endDate${identifier}`,
      endPlaceholder: !singleMode && endDate && endDate.date ? endDate.placeholder || endDate.label : '',
      onChange: () => {
        // TODO: this is required but not used. Asked in uitk-react about it and how we can get rid of it
      },
      routerLinkWrapper,
      showDatePicker: this.state.showDatePicker,
      startId: `start-date-${identifier}`,
      startInvalid: startDate.currentError,
      startLabel: startDate.label,
      startName: `startDate${identifier}`,
      startPlaceholder: startDate && startDate.date ? startDate.placeholder || startDate.label : ''
    };

    const datePickerProps: UitkNewDatePickerProps = {
      buttonText,
      dateFieldProps,
      daysBookableInAdvance: startDate.daysBookableInAdvance,
      dialogWrapper: this.dialogWrapper,
      doubleMonths: true,
      endDate: singleMode ? '' : endDate.date && endDate.date.isoFormat,
      firstDayOfWeek: DayName.SUN,
      midnightBookingOffset: 4,
      onDismissCB: this.onDismissCB,
      onOpenCB: this.onOpenCB,
      onSubmitCB: (start: Date, end: Date, isMobile: boolean) => {
        onSubmit(start, end);
        this.onSubmitCB(isMobile);
      },
      singleMode,
      singleDateSelect,
      startDate: startDate.date.isoFormat,
      onMountCB: (startRef, endRef) => this.catchRefs(startRef, endRef),
      validStartDate
    };

    return <UitkNewDatePicker {...datePickerProps} />;
  }
}
